import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  BarChart3,
  Store,
  Tag,
  Ticket,
  MessageSquare,
  Plus,
  Star,
  TrendingUp,
  Eye,
} from "lucide-react";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import DashboardShell from "@/components/DashboardShell";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { normalizeShop, normalizeCategories } from "@/lib/adapt";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const NAV = [
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "shops", label: "Shops", icon: Store },
  { key: "offers", label: "Offers", icon: Tag },
  { key: "coupons", label: "Coupons", icon: Ticket },
  { key: "messages", label: "Messages", icon: MessageSquare },
];

export default function OwnerDashboard() {
  const { user } = useAuth();
  const [active, setActive] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [shops, setShops] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [messages, setMessages] = useState([]);
  const [categories, setCategories] = useState([]);

  const [shopForm, setShopForm] = useState({ name: "", category: "Bakery", description: "", address: "", phone: "" });
  const [offerForm, setOfferForm] = useState({ shop_id: "", title: "", description: "", discount: "", valid_days: 30 });

  const load = async () => {
    try {
      const [a, s, c, m, cat] = await Promise.all([
        api.get("/owner/analytics"),
        api.get("/shops/mine"),
        api.get("/owner/coupons"),
        api.get("/messages/inbox"),
        api.get("/categories"),
      ]);
      setAnalytics(a.data);
      const norm = (s.data || []).map(normalizeShop);
      setShops(norm);
      setCoupons(c.data?.coupons || []);
      setMessages(m.data?.threads || []);
      setCategories(normalizeCategories(cat.data?.categories));
      if (norm[0]) setOfferForm((f) => ({ ...f, shop_id: f.shop_id || norm[0].id }));
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createShop = async () => {
    if (!shopForm.name) return toast.error("Shop name required");
    if (!shopForm.address) return toast.error("Address required");
    try {
      await api.post("/shops", {
        ...shopForm,
        lat: 12.9716,
        lng: 77.5946,
      });
      toast.success("Shop submitted for approval");
      setShopForm({ name: "", category: "Bakery", description: "", address: "", phone: "" });
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    }
  };

  const createOffer = async () => {
    if (!offerForm.shop_id) return toast.error("Create a shop first");
    if (!offerForm.title) return toast.error("Offer title required");
    try {
      await api.post("/offers", {
        shop_id: offerForm.shop_id,
        title: offerForm.title,
        description: offerForm.description,
        discount_percent: offerForm.discount ? Number(offerForm.discount) : null,
      });
      toast.success("Offer published");
      setOfferForm((f) => ({ ...f, title: "", description: "", discount: "" }));
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    }
  };

  return (
    <DashboardShell
      title="Owner dashboard"
      subtitle="Manage your shops, offers and customers."
      nav={NAV}
      active={active}
      onNav={setActive}
    >
      {active === "analytics" && analytics && (
        <div className="space-y-6" data-testid="analytics-panel">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Shops" value={analytics.totals?.shops ?? 0} icon={Store} />
            <Stat label="Active offers" value={analytics.totals?.offers ?? 0} icon={Tag} />
            <Stat label="Total views" value={analytics.totals?.views_total ?? 0} icon={Eye} />
            <Stat label="Messages" value={analytics.totals?.messages ?? 0} icon={MessageSquare} />
          </div>

          <div className="bg-card rounded-3xl border border-border p-6">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="w-5 h-5 text-primary" />
              <h3 className="font-display font-bold text-lg">Views this week</h3>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={analytics.daily_views || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5DACE" vertical={false} />
                <XAxis dataKey="label" stroke="#63574A" tickLine={false} axisLine={false} />
                <YAxis stroke="#63574A" tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: "rgba(198,81,47,0.08)" }}
                  contentStyle={{ borderRadius: 14, border: "1px solid #E5DACE", background: "#FBF6F0" }}
                />
                <Bar dataKey="views" fill="#C6512F" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {active === "shops" && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-display font-bold text-xl">Your shops</h3>
            <Dialog>
              <DialogTrigger asChild>
                <button
                  data-testid="add-shop-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-5 py-2.5 hover:bg-primary/90 transition-colors"
                >
                  <Plus className="w-4 h-4" /> Add shop
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-display">New shop</DialogTitle>
                  <DialogDescription>Add your shop details. It'll go live once an admin approves it.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <Inp placeholder="Shop name" value={shopForm.name} onChange={(v) => setShopForm({ ...shopForm, name: v })} testid="shop-name-input" />
                  <Select value={shopForm.category} onValueChange={(v) => setShopForm({ ...shopForm, category: v })}>
                    <SelectTrigger data-testid="shop-cat-input"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Inp placeholder="Address" value={shopForm.address} onChange={(v) => setShopForm({ ...shopForm, address: v })} testid="shop-addr-input" />
                  <Inp placeholder="Phone" value={shopForm.phone} onChange={(v) => setShopForm({ ...shopForm, phone: v })} testid="shop-phone-input" />
                  <Inp placeholder="Description" value={shopForm.description} onChange={(v) => setShopForm({ ...shopForm, description: v })} testid="shop-desc-input" />
                  <button data-testid="create-shop-submit" onClick={createShop} className="w-full rounded-full bg-primary text-primary-foreground font-semibold py-3 hover:bg-primary/90 transition-colors">
                    Submit for approval
                  </button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="owner-shops">
            {shops.map((s) => (
              <div key={s.id} className="bg-card rounded-3xl border border-border overflow-hidden">
                <div className="h-36 overflow-hidden">
                  <img src={s.images?.[0]} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <h4 className="font-display font-bold text-lg">{s.name}</h4>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${s.approved ? "bg-success/15 text-success" : "bg-primary/10 text-primary"}`}>
                      {s.approved ? "live" : "pending"}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground capitalize mt-1">{s.category}</p>
                  <div className="flex items-center gap-1 mt-3 text-sm">
                    <Star className="w-4 h-4 fill-primary text-primary" /> {s.rating} ({s.review_count})
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {active === "offers" && (
        <div className="max-w-lg bg-card rounded-3xl border border-border p-8" data-testid="offer-form">
          <h3 className="font-display font-bold text-xl mb-5">Publish an offer</h3>
          <div className="space-y-3">
            <Select value={offerForm.shop_id} onValueChange={(v) => setOfferForm({ ...offerForm, shop_id: v })}>
              <SelectTrigger data-testid="offer-shop-select"><SelectValue placeholder="Select shop" /></SelectTrigger>
              <SelectContent>
                {shops.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Inp placeholder="Offer title" value={offerForm.title} onChange={(v) => setOfferForm({ ...offerForm, title: v })} testid="offer-title-input" />
            <Inp placeholder="Discount % (e.g. 20)" value={offerForm.discount} onChange={(v) => setOfferForm({ ...offerForm, discount: v })} testid="offer-discount-input" />
            <Inp placeholder="Description" value={offerForm.description} onChange={(v) => setOfferForm({ ...offerForm, description: v })} testid="offer-desc-input" />
            <button data-testid="create-offer-submit" onClick={createOffer} className="w-full rounded-full bg-primary text-primary-foreground font-semibold py-3 hover:bg-primary/90 transition-colors">
              Publish offer
            </button>
          </div>
        </div>
      )}

      {active === "coupons" && (
        <div className="bg-card rounded-3xl border border-border overflow-hidden" data-testid="owner-coupons">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left">
              <tr>
                <Th>Code</Th><Th>Title</Th><Th>Shop</Th><Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => (
                <tr key={c.id} className="border-t border-border">
                  <Td className="font-mono font-bold">{c.code}</Td>
                  <Td>{c.title}</Td>
                  <Td>{c.shop_name}</Td>
                  <Td>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${c.status === "redeemed" ? "bg-secondary text-muted-foreground" : "bg-success/15 text-success"}`}>
                      {c.status}
                    </span>
                  </Td>
                </tr>
              ))}
              {coupons.length === 0 && <tr><Td className="text-muted-foreground py-8" colSpan={4}>No coupons issued yet.</Td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {active === "messages" && (
        <div className="space-y-3 max-w-2xl" data-testid="owner-messages">
          {messages.length === 0 ? (
            <p className="text-muted-foreground">No customer messages yet.</p>
          ) : (
            messages.map((t) => (
              <div key={`${t.shop_id}-${t.customer_id}`} className="bg-card rounded-2xl border border-border p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold">{t.customer_name} · {t.shop_name}</span>
                  <span className="text-xs text-muted-foreground">{t.last_at ? new Date(t.last_at).toLocaleDateString() : ""}</span>
                </div>
                <p className="text-foreground/90">{t.last_text}</p>
              </div>
            ))
          )}
        </div>
      )}
    </DashboardShell>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-card rounded-3xl border border-border p-6">
      <span className="grid place-items-center w-11 h-11 rounded-2xl bg-primary/10 text-primary mb-4">
        <Icon className="w-5 h-5" />
      </span>
      <p className="font-display font-extrabold text-3xl tracking-tight">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

function Inp({ placeholder, value, onChange, testid }) {
  return (
    <input
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-2xl bg-secondary border border-border px-4 py-3 font-medium focus:outline-none focus:border-primary transition-colors"
    />
  );
}

function Th({ children }) {
  return <th className="px-5 py-4 font-bold text-muted-foreground uppercase text-xs tracking-wider">{children}</th>;
}
function Td({ children, className = "", colSpan }) {
  return <td colSpan={colSpan} className={`px-5 py-4 ${className}`}>{children}</td>;
}
